import { useMemo } from 'react';

import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';

import { Bets } from '../../../config/bets';
import { getDotOrCommaIndex, hasDotOrComma } from '../../../config/utils';
import { useAutoCashoutSlice } from '../../../store/slices/autocashout/selectors';

interface ValidCashoutMultiplierObj {
  inputFieldValue: string;
  actualMultplier: number;
}

export const useValidCashoutMultiplierConnected = (betId: Bets): ValidCashoutMultiplierObj => {
  const autoCashoutObj = useAutoCashoutSlice.getAutoCashoutObj(betId);
  const customMultiplierCap = useConfigSlice.tableConfig();
  const cashoutMaxLimit = customMultiplierCap?.customMultiplierCap ? customMultiplierCap.customMultiplierCap : 100;
  const cashoutMinLimit = 1.01;

  const convertInputValueToAmount = (value: string): number => {
    const finalInputFieldValue = value.replace(',', '.');
    let actualMultplier = +finalInputFieldValue;
    //Entered input is only Dot or comma
    if (!finalInputFieldValue || finalInputFieldValue === '.') {
      actualMultplier = cashoutMinLimit;
    }
    const iscashoutMaxLimitExceed = actualMultplier > cashoutMaxLimit;
    const isBetLessThancashoutMinLimit = actualMultplier < cashoutMinLimit;
    //Entered input less than tableMin limit
    if (isBetLessThancashoutMinLimit) {
      actualMultplier = cashoutMinLimit;
    }
    //Entered input more than tableMax limit
    if (iscashoutMaxLimitExceed) {
      //Setting available balance or table max limit
      actualMultplier = cashoutMaxLimit;
    }
    return actualMultplier;
  };

  return useMemo(() => {
    let basicValidatedString = autoCashoutObj.cashoutMultiplier?.replace(/[^0-9.,]/g, '') ?? '';
    const dotOrCommaIndex = getDotOrCommaIndex(basicValidatedString);
    const isDotOrCommaExisted = hasDotOrComma(basicValidatedString);
    const stringAfterTheDotOrComma = basicValidatedString.slice(dotOrCommaIndex + 1);
    const secondDotOrCommaIndex = getDotOrCommaIndex(stringAfterTheDotOrComma);

    if (isDotOrCommaExisted) {
      //When second dot or comma entered
      if (secondDotOrCommaIndex >= 0) {
        basicValidatedString = basicValidatedString.slice(0, dotOrCommaIndex + secondDotOrCommaIndex + 1);
        basicValidatedString = basicValidatedString.slice(0, dotOrCommaIndex + 3);
      }
      //Correcting to 2 decimal points
      else {
        basicValidatedString = basicValidatedString.slice(0, dotOrCommaIndex + 3);
      }
    }

    return {
      inputFieldValue: basicValidatedString,
      actualMultplier: convertInputValueToAmount(basicValidatedString),
    };
  }, [autoCashoutObj]);
};
